/**
 * The own picture of a visitor of the public tool. It never leaves the browser (spec §2): the
 * document holds only a local `blob:` address, which ends with the page. The bytes stay in
 * IndexedDB (one record), so a reload and the page after a sign-in still have the picture.
 */
const DATABASE = 'm24CoverMaker';
const STORE = 'upload';
const KEY = 'background';

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const request = indexedDB.open(DATABASE, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      // A private window can refuse the database: the picture then lives for this visit only.
      resolve(null);
    }
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return open().then(
    (database) =>
      new Promise<T | null>((resolve) => {
        if (database === null) {
          resolve(null);
          return;
        }
        try {
          const request = work(database.transaction(STORE, mode).objectStore(STORE));
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      })
  );
}

/** Keeps the picture. A failure is silent: the picture still shows in this visit. */
export async function savePublicUpload(file: Blob): Promise<void> {
  await run('readwrite', (store) => store.put(file, KEY));
}

/** The kept picture, or null. */
export async function loadPublicUpload(): Promise<Blob | null> {
  const value = await run<unknown>('readonly', (store) => store.get(KEY));
  return value instanceof Blob ? value : null;
}
